// Pure-logic helpers for keyboard-shortcut handling. JSX-free so the
// node test pool can verify the rules without a DOM.
//
// The two responsibilities here:
//   1. Decide whether a given event should fire shortcuts, given the
//      currently focused element type. Typing into an <input>,
//      <textarea>, contenteditable, or selecting a select element
//      should NEVER trigger a shortcut — that turns the user's typing
//      into surprising navigation.
//   2. Resolve a "G-prefix" two-key shortcut: pressing G starts a
//      ~1s window during which the next character routes to the
//      matching destination (D=dashboard, O=opportunities, etc.)
//      instead of typing.
//
// v1.4 vocabulary update: G+S → G+O for Opportunities (was Sales) and
// G+I → G+P for Processing (was In Progress). G+O loses Ongoing Care
// to Opportunities; Ongoing Care moves to G+G (double-tap inside the
// G-prefix window). The mnemonic now matches the user-facing label.

export interface ShortcutKeyEvent {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
  // The element the event originated from. We only inspect tagName
  // and isContentEditable, so the type stays minimal for testing.
  target?: {
    tagName?: string;
    isContentEditable?: boolean;
  } | null;
}

const TYPING_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

// True when a focused element is consuming keystrokes. Shortcuts must
// not preempt typing — Cmd-prefixed shortcuts are still fine because
// modifier-required shortcuts unambiguously belong to the chrome.
export function eventTargetsTypingSurface(event: ShortcutKeyEvent): boolean {
  const target = event.target;
  if (!target) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName?.toUpperCase();
  if (!tag) return false;
  return TYPING_TAGS.has(tag);
}

export type GoToDestination =
  | "dashboard"
  | "households"
  | "contacts"
  | "opportunities"
  | "processing"
  | "ongoing_care"
  | "automations"
  | "reports";

// G-prefix routing table, per shared-chrome-v2 §8 + the v1.4
// vocabulary update (2026-04-29). Letters now match the user-facing
// label: O=Opportunities, P=Processing. Ongoing Care moves to G+G
// (double-tap G inside the prefix window) since O is taken. N is
// reserved for Reports ("aNalytics").
const GO_TO_KEYS: Record<string, GoToDestination> = {
  d: "dashboard",
  h: "households",
  c: "contacts",
  o: "opportunities",
  p: "processing",
  g: "ongoing_care",
  a: "automations",
  n: "reports",
};

// Resolve the second key of a G-prefix combo to a destination, OR
// null if it isn't a known mapping. Case-insensitive.
export function resolveGoToKey(key: string): GoToDestination | null {
  if (!key) return null;
  const lower = key.toLowerCase();
  return GO_TO_KEYS[lower] ?? null;
}

export const GO_TO_ROUTES: Record<GoToDestination, string> = {
  dashboard: "/dashboard",
  households: "/households",
  contacts: "/contacts",
  opportunities: "/opportunities",
  processing: "/processing",
  ongoing_care: "/ongoing-care",
  automations: "/automations",
  reports: "/reports",
};

// Window in ms for the second key of a G-prefix shortcut. Long
// enough that someone hitting GD doesn't have to be a virtuoso, short
// enough that an incidental G + delay + D doesn't navigate.
export const G_PREFIX_TIMEOUT_MS = 1000;

// ── Platform-aware shortcut LABELS (S2 PR 4, audit 8.5) ─────────────
//
// The handler above already accepts metaKey OR ctrlKey; only the
// DISPLAY was mac-glyph-only ("⌘K" on Grant's Windows machines).
// Pure detector takes the platform string for testability; the
// browser-reading wrapper feeds it navigator values.

export function isMacLikePlatform(platformString: string): boolean {
  return /mac|iphone|ipad|ipod/i.test(platformString);
}

export function detectMacLikePlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  const uaData = (
    navigator as Navigator & {
      userAgentData?: { platform?: string };
    }
  ).userAgentData;
  return isMacLikePlatform(
    uaData?.platform || navigator.platform || navigator.userAgent || "",
  );
}

export function shortcutModifierLabel(isMacLike: boolean): "⌘" | "Ctrl" {
  return isMacLike ? "⌘" : "Ctrl";
}

export type GlobalShortcut =
  // ⌘K opens Ask Stew (rebound from open_search as part of Ask Stew
  // V1 PR #3 — GlobalSearch retires; Stew's Ask mode subsumes
  // typed-name lookup via the exact-match short-circuit in
  // shared/ai/stew-intent-router.ts).
  | { kind: "open_ask_stew" }
  | { kind: "open_cheat_sheet" }
  | { kind: "open_preferences" }
  | { kind: "toggle_sidebar" }
  | { kind: "close_overlay" };

// Resolve a single keystroke (NOT mid-G-prefix) to a global chrome
// action. Returns null if it doesn't match.
//
// Cmd-prefixed shortcuts use either metaKey (mac) or ctrlKey (other).
// The pure helper doesn't care which; the runtime layer wires the
// keydown event in.
export function resolveGlobalShortcut(
  event: ShortcutKeyEvent,
): GlobalShortcut | null {
  const cmd = event.metaKey || event.ctrlKey;

  if (cmd && event.key.toLowerCase() === "k") {
    return { kind: "open_ask_stew" };
  }
  if (cmd && event.key === "/") {
    return { kind: "toggle_sidebar" };
  }
  if (cmd && event.key === ",") {
    return { kind: "open_preferences" };
  }
  // Bare ? key (shift+/ on US keyboard yields '?').
  if (!cmd && event.key === "?") {
    return { kind: "open_cheat_sheet" };
  }
  if (!cmd && event.key === "Escape") {
    return { kind: "close_overlay" };
  }
  return null;
}

// Recognize a bare G keypress that should start a G-prefix window.
// G alone (no modifier) starts the prefix; otherwise it's just typing
// (and eventTargetsTypingSurface should already have ruled it out).
export function isGPrefixStart(event: ShortcutKeyEvent): boolean {
  if (event.metaKey || event.ctrlKey || event.altKey) return false;
  return event.key.toLowerCase() === "g";
}
